import {
  get_name_initials,
  is_group_conversation_record,
  normalize_chat_member,
  same_id,
  unique_visible_members,
} from './floating_chat_widget_helpers.js';

export const floating_chat_display_methods = {
  conversation_matches_query(conversation, query) {
    if (!query) return true;
    return [
      this.get_conversation_name(conversation),
      this.get_conversation_subtitle(conversation),
    ].join(' ').toLowerCase().includes(query);
  },

  message_matches_query(message, query) {
    if (!query) return true;
    return [
      message.content,
      this.get_user_name(message.sender),
      ...(message.attachments || []).map(file => this.get_chat_file_name(file)),
    ].filter(Boolean).join(' ').toLowerCase().includes(query);
  },

  get_message_search_preview(message) {
    return message?.content
      || (message?.attachments?.length ? `Attachment: ${this.get_chat_file_name(message.attachments[0])}` : 'Message');
  },

  jump_to_message(message) {
    this.search_overlay_open = false;
    this.$nextTick(() => {
      const target = this.$refs.message_scroller?.querySelector(`[data-message-id="${message.id}"]`);
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  },

  is_visible_conversation(conversation) {
    if (!conversation) return false;
    if (conversation.hidden_for_me || conversation.is_hidden_for_me) return false;
    return Boolean(conversation.last_message || same_id(conversation.id, this.active_conversation_id) || this.is_group_conversation(conversation) || conversation.cleared_for_me);
  },

  is_my_message(message) {
    return same_id(message?.sender?.id, this.current_user_id);
  },

  is_group_conversation(conversation) {
    return is_group_conversation_record(conversation);
  },

  is_space_conversation(conversation) {
    return this.is_group_conversation(conversation);
  },

  get_other_user(conversation) {
    const participants = conversation?.participants || conversation?.members || [];
    const normalized = participants.map(participant => participant?.user || participant).filter(Boolean);
    return normalized.find(user => !same_id(user.id, this.current_user_id))
      || normalized[0]
      || conversation?.recipient
      || conversation?.other_user
      || conversation?.user
      || null;
  },

  get_conversation_name(conversation) {
    if (!conversation) return '';
    if (this.is_group_conversation(conversation)) {
      return conversation.name || conversation.title || conversation.group_name || conversation.group?.name || conversation.space_name || 'Group';
    }
    return this.get_user_name(this.get_other_user(conversation));
  },

  get_conversation_subtitle(conversation) {
    const last_message = conversation?.last_message;
    if (!last_message && (conversation?.cleared_for_me || conversation?.is_cleared_for_me)) return 'No messages yet';
    const snippet = last_message?.content || (last_message?.attachments?.length ? 'Attachment' : 'No messages yet');
    if (!last_message || !this.is_group_conversation(conversation)) return snippet;
    const sender = this.get_user_name(last_message.sender);
    return `${this.get_initials(sender)}: ${snippet}`;
  },

  get_conversation_members(conversation) {
    const raw = conversation?.participants || conversation?.members || conversation?.group?.members || [];
    return unique_visible_members(raw.map(member => this.normalize_member(member)));
  },

  normalize_member(raw_member) {
    return normalize_chat_member(raw_member, this.available_users, this.current_user_id, this.get_user_name);
  },

  get_user_name(user) {
    return `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
      || user?.full_name
      || user?.name
      || user?.username
      || user?.email
      || 'Unknown';
  },

  get_initials(name) {
    return get_name_initials(name);
  },

  format_time(value) {
    if (!value) return '';
    return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  },

  format_time_ago(value) {
    if (!value) return '';
    const diff = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric' });
  },

  is_image_file(file_name) {
    if (!file_name) return false;
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(file_name.split('.').pop().toLowerCase());
  },

  is_video_file(file_name) {
    if (!file_name) return false;
    return ['mp4', 'webm', 'ogg', 'ogv', 'mov'].includes(file_name.split('.').pop().toLowerCase());
  },

  is_audio_file(file_name) {
    if (!file_name) return false;
    return ['mp3', 'wav', 'ogg', 'oga', 'aac', 'm4a', 'flac', 'opus', 'weba'].includes(file_name.split('.').pop().toLowerCase());
  },

  stop_other_media(event) {
    const current = event.target;
    const scroller = this.$refs.message_scroller;
    if (!scroller) return;
    scroller.querySelectorAll('audio, video').forEach(media => {
      if (media !== current && !media.paused) media.pause();
    });
  },

  preview_chat_file(file) {
    const url = this.get_chat_file_url(file);
    if (url) window.open(url, '_blank', 'noopener');
  },

  file_icon_chat(file_name) {
    const ext = (file_name || '').split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) return 'far fa-file-image text-success';
    if (ext === 'pdf') return 'far fa-file-pdf text-danger';
    if (['doc', 'docx'].includes(ext)) return 'far fa-file-word text-primary';
    if (['mp4', 'webm', 'ogg', 'ogv', 'mov'].includes(ext)) return 'fas fa-film text-warning';
    if (['mp3', 'wav', 'aac', 'm4a', 'flac', 'opus'].includes(ext)) return 'fas fa-music text-info';
    return 'far fa-file-alt text-secondary';
  },

  get_chat_file_name(file) {
    return file?.file_name || file?.name || file?.filename || 'Attachment';
  },

  get_chat_file_url(file) {
    return file?.file_url || file?.url || file?.presigned_url || file?.download_url || file?.attachment_url || '';
  },

  patch_message(message_id, patch) {
    this.messages = this.messages.map(item => (
      same_id(item.id, message_id) ? { ...item, ...patch } : item
    ));
  },

  upsert_conversation(conversation) {
    if (!conversation?.id) return;
    const existing_index = this.conversations.findIndex(item => same_id(item.id, conversation.id));
    if (existing_index !== -1) {
      const existing = this.conversations.find(item => same_id(item.id, conversation.id));
      this.conversations.splice(existing_index, 1, {
        ...existing,
        ...conversation,
        participants: conversation.participants?.length ? conversation.participants : existing.participants,
        member_count: conversation.member_count || existing.member_count,
      });
    } else {
      this.conversations.unshift(conversation);
    }
  },
};
